/**
 * Phase 19.10 — Beat Playlist detail page.
 *
 * Shows playlist metadata + ordered beats, with:
 *   - Play all (queues beats into global PlayerProvider — Phase 19.8)
 *   - Save / Unsave (other users' playlists)
 *   - Edit / Rename / Delete (owner only)
 *   - Remove beat (owner only)
 *   - Analytics tile (owner only — plays + saves + per-beat plays)
 *   - Share link
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";
import api, { BACKEND_URL } from "@/lib/api";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useShareVisibility } from "@/lib/useShareVisibility";
import { usePlayer } from "@/components/player/PlayerProvider";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const fmtPrice = (p) => (p == null || p === 0 ? "Free" : `$${Number(p).toFixed(2)}`);

const BeatPlaylistDetailPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const { currentUser: user } = useCurrentUser();
  const player = usePlayer();

  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [saved, setSaved] = useState(false);
  const [editName, setEditName] = useState("");
  const [editing, setEditing] = useState(false);

  const isOwner = !!user && playlist?.user_id === user.user_id;

  const fetchPlaylist = useCallback(() => {
    setLoading(true);
    api.get(`/beat-playlists/${playlistId}`)
      .then((r) => { setPlaylist(r.data); setEditName(r.data?.name || ""); })
      .catch((err) => {
        const d = err?.response?.data?.detail;
        toast.error(d || "Could not load playlist");
        if (err?.response?.status === 404) navigate("/beat-playlists");
      })
      .finally(() => setLoading(false));
  }, [playlistId, navigate]);

  useEffect(() => { fetchPlaylist(); }, [fetchPlaylist]);

  // Owner-only analytics
  useEffect(() => {
    if (!isOwner) { setAnalytics(null); return; }
    api.get(`/beat-playlists/${playlistId}/analytics`)
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }, [isOwner, playlistId]);

  // Track "saved" state for non-owners
  useEffect(() => {
    if (!user || isOwner) { setSaved(false); return; }
    api.get("/beat-playlists/saved").then((r) => {
      const ids = (r.data?.playlists || []).map((p) => p.playlist_id);
      setSaved(ids.includes(playlistId));
    }).catch(() => {});
  }, [user, isOwner, playlistId]);

  const handlePlayAll = () => {
    const beats = playlist?.beats || [];
    if (!beats.length) return;
    // Normalize so PlayerProvider can play beats too — pipe audio_url + cover_url
    const queue = beats
      .filter((b) => b.audio_url)
      .map((b) => ({
        trackId: `beat_${b.beat_id}`,
        beat_id: b.beat_id,
        title: b.title,
        artist_name: b.producer?.artist_name || b.producer?.name || "Producer",
        artist_id: b.producer?.user_id || b.user_id,
        cover_url: b.cover_url,
        audio_url: b.audio_url,
        kind: "beat",
      }));
    if (!queue.length) {
      toast.error("No playable beats in this playlist");
      return;
    }
    player.play(queue[0], { queue, sourceLabel: `🎼 ${playlist?.name || "Playlist"}` });
    api.post(`/beat-playlists/${playlistId}/play`).catch(() => {});
  };

  const handleSaveToggle = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/beat-playlists/${playlistId}/save`);
        setSaved(false);
        toast.success("Removed from saved");
      } else {
        await api.post(`/beat-playlists/${playlistId}/save`);
        setSaved(true);
        toast.success("Saved to your library");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save playlist");
    } finally { setBusy(false); }
  };

  const handleRemoveBeat = async (beatId) => {
    if (!isOwner) return;
    if (!window.confirm("Remove this beat from the playlist?")) return;
    try {
      await api.delete(`/beat-playlists/${playlistId}/beats/${beatId}`);
      toast.success("Beat removed");
      fetchPlaylist();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const handleSaveEdit = async () => {
    if (!isOwner) return;
    if (!editName.trim() || editName.trim() === playlist?.name) {
      setEditing(false); return;
    }
    try {
      const r = await api.patch(`/beat-playlists/${playlistId}`, { name: editName.trim() });
      setPlaylist(r.data); setEditing(false);
      toast.success("Playlist renamed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rename");
    }
  };

  const handleDelete = async () => {
    if (!isOwner) return;
    if (!window.confirm(`Delete "${playlist?.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/beat-playlists/${playlistId}`);
      toast.success("Playlist deleted");
      navigate("/beat-playlists");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const { canShare } = useShareVisibility();

  const handleShare = async () => {
    // Prefer the canonical OG-rich share URL so Facebook/WhatsApp/etc.
    // render the playlist cover + title instead of the site default.
    const canonical = `${BACKEND_URL}/api/share/playlist/${playlistId}`;
    const title = playlist?.name || "RGM Playlist";
    try {
      if (navigator.share) {
        await navigator.share({ title, url: canonical });
        return;
      }
      await navigator.clipboard.writeText(canonical);
      toast.success("Playlist link copied!");
    } catch {
      toast.error("Could not share");
    }
  };

  const beats = useMemo(() => playlist?.beats || [], [playlist]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center"
           data-testid="bpd-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
      </div>
    );
  }
  if (!playlist) return null;

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="beat-playlist-detail-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="mb-4">
          <BackButton fallback="/beat-playlists" label="Playlists" />
        </div>

        {/* Header */}
        <div className="flex items-end gap-5 flex-wrap mb-8">
          <div className="w-44 h-44 rounded-xl overflow-hidden bg-gradient-to-br from-purple-900/40 to-pink-900/40 flex-shrink-0">
            {playlist.cover_image && (
              <img src={optimizeImage(playlist.cover_image, 'thumb')} alt=""
                   className="w-full h-full object-cover" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">
              🎼 Beat Playlist {playlist.featured ? "· ⭐ Featured" : ""}
            </p>
            {editing ? (
              <div className="flex gap-2 mt-1 max-w-md">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="bg-black/40 text-white border-gray-700"
                  autoFocus
                  data-testid="bpd-edit-name-input"
                />
                <Button onClick={handleSaveEdit} className="bg-purple-600 hover:bg-purple-500 text-white">
                  Save
                </Button>
                <Button variant="outline" onClick={() => { setEditing(false); setEditName(playlist.name); }}
                        className="border-gray-700 text-white">Cancel</Button>
              </div>
            ) : (
              <h1 className="text-3xl sm:text-4xl font-black text-white truncate"
                  data-testid="bpd-name">
                {playlist.name}
              </h1>
            )}
            <p className="text-sm text-gray-300 mt-1 max-w-2xl">{playlist.description}</p>
            <p className="text-[11px] text-gray-400 mt-2">
              by <Link to={`/artist/${playlist.creator?.user_id}`}
                       className="text-purple-300 hover:underline">
                {playlist.creator?.artist_name || playlist.creator?.name || "Producer"}
              </Link>
              {" · "}
              <span>{beats.length} beats</span>
              {" · "}
              <span>▶ {playlist.plays || 0}</span>
              {" · "}
              <span>🔖 {playlist.saves || 0}</span>
            </p>
            <div className="flex gap-2 mt-4 flex-wrap">
              <Button
                onClick={handlePlayAll}
                disabled={!beats.length}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid="bpd-play-all"
              >
                ▶ Play All
              </Button>
              {!isOwner && user && (
                <Button
                  onClick={handleSaveToggle}
                  disabled={busy}
                  variant="outline"
                  className={saved
                    ? "border-emerald-400/50 text-emerald-200"
                    : "border-purple-500/40 text-white"}
                  data-testid="bpd-save-toggle"
                >
                  {saved ? "✓ Saved" : "🔖 Save"}
                </Button>
              )}
              {canShare("playlists") && (
                <Button onClick={handleShare} variant="outline"
                  title="Share this playlist"
                  data-testid="bpd-share-btn"
                  className="border-gray-700 text-white">
                  ↗ Share
                </Button>
              )}
              {isOwner && !editing && (
                <>
                  <Button onClick={() => setEditing(true)} variant="outline"
                          className="border-gray-700 text-white" data-testid="bpd-edit">
                    ✎ Rename
                  </Button>
                  <Button onClick={handleDelete} variant="outline"
                          className="border-rose-500/40 text-rose-200" data-testid="bpd-delete">
                    🗑 Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Owner-only analytics */}
        {isOwner && analytics && (
          <Card className="p-4 mb-6 bg-amber-500/5 border-amber-500/30"
                data-testid="bpd-analytics">
            <p className="text-[11px] uppercase tracking-widest text-amber-300 font-bold mb-2">
              📈 Playlist Analytics
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[10px] text-gray-400 uppercase">Plays</p>
                <p className="text-2xl font-black text-white">{analytics.plays}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase">Saves</p>
                <p className="text-2xl font-black text-white">{analytics.saves}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase">Beats</p>
                <p className="text-2xl font-black text-white">{analytics.beat_count}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Beat list */}
        {beats.length === 0 ? (
          <Card className="p-8 text-center text-gray-400 bg-gray-900/40 border-gray-800">
            <p className="text-lg text-white">No beats in this playlist yet.</p>
            {isOwner && (
              <Link to="/beats" className="text-purple-300 underline mt-2 inline-block">
                Browse the marketplace →
              </Link>
            )}
          </Card>
        ) : (
          <div className="space-y-2" data-testid="bpd-beat-list">
            {beats.map((b, idx) => (
              <Card
                key={b.beat_id}
                className="p-3 bg-white/[0.03] border-white/10 hover:border-purple-400/40 transition flex items-center gap-3"
                data-testid={`bpd-beat-row-${b.beat_id}`}
              >
                <span className="text-gray-500 text-xs w-6 text-right">{idx + 1}</span>
                <div className="w-12 h-12 rounded-md bg-black/50 overflow-hidden flex-shrink-0">
                  {b.cover_url && <img src={optimizeImage(b.cover_url, 'avatar')} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/beats/${b.beat_id}`}
                        className="text-sm font-bold text-white truncate hover:text-pink-300 block">
                    {b.title}
                  </Link>
                  <p className="text-[11px] text-gray-400 truncate">
                    {b.producer?.artist_name || b.producer?.name || "Producer"}
                    {b.bpm ? ` · ${b.bpm} BPM` : ""}
                    {b.genre ? ` · ${b.genre}` : ""}
                  </p>
                </div>
                <span className="text-xs text-pink-300 font-bold">{fmtPrice(b.price)}</span>
                <button
                  onClick={() => {
                    const queue = beats.filter((x) => x.audio_url).map((x) => ({
                      trackId: `beat_${x.beat_id}`, beat_id: x.beat_id, title: x.title,
                      artist_name: x.producer?.artist_name || x.producer?.name || "Producer",
                      artist_id: x.producer?.user_id || x.user_id,
                      cover_url: x.cover_url, audio_url: x.audio_url, kind: "beat",
                    }));
                    const found = queue.findIndex((q) => q.beat_id === b.beat_id);
                    if (found < 0) return;
                    player.play(queue[found], { queue, sourceLabel: `🎼 ${playlist?.name || "Playlist"}` });
                    api.post(`/beat-playlists/${playlistId}/play`).catch(() => {});
                  }}
                  className="text-purple-300 hover:text-pink-300 px-3 text-xl"
                  aria-label="Play"
                  data-testid={`bpd-beat-play-${b.beat_id}`}
                >▶</button>
                {isOwner && (
                  <button
                    onClick={() => handleRemoveBeat(b.beat_id)}
                    className="text-gray-500 hover:text-rose-300 px-2 text-sm"
                    aria-label="Remove from playlist"
                    data-testid={`bpd-beat-remove-${b.beat_id}`}
                  >✕</button>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeatPlaylistDetailPage;
